// 転換率・売上分析画面の純関数群（フォーマット・前期間比・nice max・粒度自動設定・ソート）。
// design/design_handoff_stoq_conversion/STOQ-conversion-analytics.dc.html の
// renderVals() 内ロジック（yen/pct/roas/dlt/nice/バケット幅 等）を TS に移植。
import type { Channel, Metric } from "../types/analytics";

export type Preset = "7d" | "30d" | "tm" | "lm" | "90d" | "all" | "custom";

export const PRESET_LABEL: Record<Exclude<Preset, "custom">, string> = {
  "7d": "過去7日",
  "30d": "過去30日",
  tm: "今月",
  lm: "先月",
  "90d": "過去90日",
  all: "全期間",
};

export const CHANNEL_LABEL: Record<Channel, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
export const CHANNEL_COLOR: Record<Channel, string> = { shopify: "#5E8E3E", rakuten: "#BF1E2E", amazon: "#E3900B" };
export const CHANNEL_ORDER: Channel[] = ["shopify", "rakuten", "amazon"];

const MS_DAY = 86400000;

function toUtcMs(d: string): number {
  const [y, m, day] = d.split("-").map(Number);
  return Date.UTC(y, m - 1, day);
}
function fromUtcMs(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
export function addDays(d: string, n: number): string {
  return fromUtcMs(toUtcMs(d) + n * MS_DAY);
}
export function diffDays(a: string, b: string): number {
  return Math.round((toUtcMs(b) - toUtcMs(a)) / MS_DAY);
}
export function weekday(d: string): number {
  return new Date(toUtcMs(d)).getUTCDay();
}
export function isWeekend(d: string): boolean {
  const w = weekday(d);
  return w === 0 || w === 6;
}

/** プリセット→期間・既定粒度（README Interactions: 7日/30日/今月/先月=日次、90日=週次、全期間=月次）。 */
export function presetRange(preset: Exclude<Preset, "custom">, today: string): { from: string; to: string; granularity: "day" | "week" | "month" } {
  const [y, m] = today.split("-").map(Number);
  if (preset === "7d") return { from: addDays(today, -6), to: today, granularity: "day" };
  if (preset === "30d") return { from: addDays(today, -29), to: today, granularity: "day" };
  if (preset === "90d") return { from: addDays(today, -89), to: today, granularity: "week" };
  if (preset === "tm") return { from: `${today.slice(0, 7)}-01`, to: today, granularity: "day" };
  if (preset === "lm") {
    const prevM = m === 1 ? 12 : m - 1;
    const prevY = m === 1 ? y - 1 : y;
    const from = `${prevY}-${String(prevM).padStart(2, "0")}-01`;
    const lastDay = new Date(Date.UTC(prevY, prevM, 0)).getUTCDate();
    const to = `${prevY}-${String(prevM).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return { from, to, granularity: "day" };
  }
  // all: 全期間。実データの開始日が不明なmock/実運用両対応のため、十分に広い過去日を使う。
  return { from: "2026-01-01", to: today, granularity: "month" };
}

// ---- フォーマット ----
export function yen(n: number): string {
  const sign = n < 0 ? "−¥" : "¥";
  return sign + Math.round(Math.abs(n)).toLocaleString("ja-JP");
}

export function man(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 10000) {
    return (n < 0 ? "−¥" : "¥") + (abs / 10000).toLocaleString("ja-JP", { maximumFractionDigits: 1, minimumFractionDigits: 1 }) + "万";
  }
  return yen(n);
}

export function int(n: number): string {
  return Math.round(n).toLocaleString("ja-JP");
}

export function pctStr(a: number, b: number, digits = 2): string {
  return b > 0 ? ((a / b) * 100).toFixed(digits) + "%" : "—";
}

export function roasStr(sales: number, adSpend: number): string {
  return adSpend > 0 ? Math.round((sales / adSpend) * 100).toLocaleString("ja-JP") + "%" : "—";
}

export interface Delta {
  text: string;
  color: string;
}

const COLOR_UP = "#3E7D4F";
const COLOR_DOWN = "#B3372B";
const COLOR_FLAT = "#9A958B";

/** 相対%での前期間比。invert=trueなら増加が赤（広告費など）。 */
export function deltaPct(cur: number, prev: number, hasPrev: boolean, invert = false): Delta {
  if (!hasPrev || !prev) return { text: "—", color: COLOR_FLAT };
  const v = ((cur - prev) / prev) * 100;
  const good = invert ? v < 0 : v > 0;
  const flat = Math.abs(v) < 0.05;
  return { text: (v >= 0 ? "+" : "") + v.toFixed(1) + "%", color: flat ? COLOR_FLAT : good ? COLOR_UP : COLOR_DOWN };
}

/** ptでの前期間比（転換率用）。 */
export function deltaPt(curOrders: number, curSessions: number, prevOrders: number, prevSessions: number, hasPrev: boolean): Delta {
  if (!hasPrev || !prevSessions) return { text: "—", color: COLOR_FLAT };
  const v = (curOrders / curSessions - prevOrders / prevSessions) * 100;
  return { text: (v >= 0 ? "+" : "") + v.toFixed(2) + "pt", color: v > 0 ? COLOR_UP : v < 0 ? COLOR_DOWN : COLOR_FLAT };
}

/** design nice()。5本刻みのY軸最大値。 */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / p;
  const step = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return step * p;
}

/** バケット数に応じた棒間隔。 */
export function barGap(n: number): string {
  return n > 60 ? "1px" : n > 20 ? "2px" : "6px";
}

/** 空Metricを合計するためのゼロ値。 */
export function zeroMetric(): Metric {
  return { sales: 0, orders: 0, sessions: null, visitors: null, ad_spend: 0, sessions_basis: "none" };
}

/** 指定チャネルだけを合算したMetric（クライアント側のチャネルチップ絞り込み用）。 */
export function sumChannels(byChannel: Record<Channel, Metric>, channels: Channel[]): Metric {
  const out = zeroMetric();
  for (const ch of channels) {
    const m = byChannel[ch];
    out.sales += m.sales;
    out.orders += m.orders;
    out.ad_spend += m.ad_spend;
    if (m.sessions != null) {
      out.sessions = (out.sessions ?? 0) + m.sessions;
      out.sessions_basis = "actual";
    }
    if (m.visitors != null) out.visitors = (out.visitors ?? 0) + m.visitors;
  }
  return out;
}

export type SortKey = "sales" | "sessions" | "orders" | "cvr" | "aov" | "ad" | "roas";

export function metricSortValue(m: Metric, key: SortKey): number {
  switch (key) {
    case "sales":
      return m.sales;
    case "sessions":
      return m.sessions ?? -1;
    case "orders":
      return m.orders;
    case "cvr":
      return m.sessions ? m.orders / m.sessions : -1;
    case "aov":
      return m.orders ? m.sales / m.orders : -1;
    case "ad":
      return m.ad_spend;
    case "roas":
      return m.ad_spend ? m.sales / m.ad_spend : -1;
    default:
      return 0;
  }
}
